/**
 * models.ts — normalized WorkRoamer data models for the Data Fusion Engine™.
 *
 * Every external API returns a different shape. The Fusion Engine normalizes
 * ALL of them into these common models so the rest of WorkRoamer never depends
 * on a provider-specific format. Every normalized object carries a data-
 * confidence envelope (`meta`) so future engines can reason about trust,
 * freshness, and provenance.
 */

// ─── Fusion domains ────────────────────────────────────────────────────────────
// One entry per supported data provider category. No provider is hardcoded — a
// domain is just a routing key; providers register themselves against it.

export type FusionDomain =
  | 'weather'
  | 'maps'
  | 'places'
  | 'restaurant'
  | 'cafe'
  | 'coworking'
  | 'housing'
  | 'hotel'
  | 'hostel'
  | 'monthlyRental'
  | 'transportation'
  | 'publicTransit'
  | 'airport'
  | 'atm'
  | 'bank'
  | 'currencyExchange'
  | 'currency'
  | 'hospital'
  | 'pharmacy'
  | 'embassy'
  | 'travelAdvisory'
  | 'internetSpeed'
  | 'airQuality'
  | 'costOfLiving'
  | 'events'
  | 'attraction'
  | 'communityReviews'
  | 'safety'
  | 'emergencyContact'
  | 'visa'
  | 'simProvider'
  | 'utility'
  | 'grocery'
  | 'laundry'
  | 'gym';

export const FUSION_DOMAINS: FusionDomain[] = [
  'weather', 'maps', 'places', 'restaurant', 'cafe', 'coworking', 'housing',
  'hotel', 'hostel', 'monthlyRental', 'transportation', 'publicTransit',
  'airport', 'atm', 'bank', 'currencyExchange', 'currency', 'hospital',
  'pharmacy', 'embassy', 'travelAdvisory', 'internetSpeed', 'airQuality',
  'costOfLiving', 'events', 'attraction', 'communityReviews', 'safety',
  'emergencyContact', 'visa', 'simProvider', 'utility', 'grocery', 'laundry', 'gym',
];

// ─── Data-confidence envelope ──────────────────────────────────────────────────

export type VerificationStatus = 'verified' | 'unverified' | 'estimated' | 'stale';

export interface DataConfidence {
  /** 0–100 — how much this record can be trusted (completeness × provider trust × freshness). */
  confidence: number;
  /** Epoch ms the record was produced/fetched. */
  lastUpdated: number;
  /** Provider id that produced the record (provenance). */
  providerSource: string;
  verificationStatus: VerificationStatus;
}

/** Base shape every normalized record shares. */
export interface FusedEntity {
  id: string;
  meta: DataConfidence;
}

// ─── Geo helper ────────────────────────────────────────────────────────────────

export interface GeoPoint {
  latitude: number;
  longitude: number;
}

// ─── Normalized domain models ──────────────────────────────────────────────────

/** Generic place — covers maps/places/atm/bank/hospital/pharmacy/embassy/
 *  attraction/grocery/laundry/gym/hotel/hostel/airport and similar POIs. */
export interface FusedPlace extends FusedEntity {
  name: string;
  category: FusionDomain;
  address?: string;
  location: GeoPoint;
  rating?: number;
  reviewCount?: number;
  priceLevel?: 1 | 2 | 3 | 4;
  distanceKm?: number;
  open24h?: boolean;
  tags: string[];
}

export interface FusedRestaurant extends FusedPlace {
  cuisine?: string;
  vegetarianFriendly?: boolean;
}

export interface FusedCoworking extends FusedPlace {
  dayPassUsd?: number;
  monthlyPassUsd?: number;
  internetMbps?: number;
  has24hAccess?: boolean;
}

export interface FusedHousing extends FusedEntity {
  title: string;
  kind: 'apartment' | 'hotel' | 'hostel' | 'monthlyRental' | 'other';
  location: GeoPoint;
  monthlyCostUsd?: number;
  nightlyCostUsd?: number;
  bedrooms?: number;
  furnished?: boolean;
  internetMbps?: number;
  minStayDays?: number;
  rating?: number;
  tags: string[];
}

export type WeatherCondition = 'clear' | 'partly-cloudy' | 'cloudy' | 'rain' | 'storm' | 'snow';

export interface FusedWeather extends FusedEntity {
  location: GeoPoint;
  tempC: number;
  /** Apparent temperature — optional: only when the provider reports it (never fabricated). */
  feelsLikeC?: number;
  highC: number;
  lowC: number;
  humidityPct: number;
  condition: WeatherCondition;
  /** Hour (0–23) rain is expected to start today, or null. */
  rainStartHour: number | null;
  airQualityIndex?: number;
  /** Local ISO datetime of today's sunrise/sunset — optional: only when the provider reports them. */
  sunriseIso?: string;
  sunsetIso?: string;
}

export interface FusedCurrency extends FusedEntity {
  baseCurrency: string;
  quoteCurrency: string;
  rate: number;
  trend: 'improved' | 'flat' | 'worsened';
  changePct: number;
}

export interface FusedTransportation extends FusedEntity {
  mode: 'metro' | 'bus' | 'tram' | 'rail' | 'ferry' | 'rideshare' | 'taxi' | 'bike' | 'walk';
  name: string;
  fromArea?: string;
  toArea?: string;
  avgCostUsd?: number;
  frequencyMinutes?: number;
  coverageScore?: number;
}

export interface FusedSafety extends FusedEntity {
  location: GeoPoint;
  /** 0–100 overall safety. */
  overallScore: number;
  /** Government-style advisory level 1 (safe) – 4 (do not travel). */
  advisoryLevel: 1 | 2 | 3 | 4;
  summary: string;
}

export interface FusedEvent extends FusedEntity {
  title: string;
  category: string;
  startsAt: number;
  endsAt?: number;
  venue?: string;
  location?: GeoPoint;
  priceUsd?: number;
}

export interface FusedCommunity extends FusedEntity {
  placeId: string;
  reviewCount: number;
  averageRating: number;
  sentiment: 'positive' | 'mixed' | 'negative';
  highlights: string[];
}

/** Scalar city-level metric (internet speed, air quality, cost of living). */
export interface FusedMetric extends FusedEntity {
  location: GeoPoint;
  metric: FusionDomain;
  /** Primary numeric value (Mbps, AQI, index points, USD, …). */
  value: number;
  unit: string;
  label: string;
}

// ─── Domain → normalized-model mapping ─────────────────────────────────────────
// Lets the engine's `fetch<D>()` return a correctly-typed record set per domain.

export interface DomainResultMap {
  weather: FusedWeather;
  maps: FusedPlace;
  places: FusedPlace;
  restaurant: FusedRestaurant;
  cafe: FusedPlace;
  coworking: FusedCoworking;
  housing: FusedHousing;
  hotel: FusedHousing;
  hostel: FusedHousing;
  monthlyRental: FusedHousing;
  transportation: FusedTransportation;
  publicTransit: FusedTransportation;
  airport: FusedPlace;
  atm: FusedPlace;
  bank: FusedPlace;
  currencyExchange: FusedPlace;
  currency: FusedCurrency;
  hospital: FusedPlace;
  pharmacy: FusedPlace;
  embassy: FusedPlace;
  travelAdvisory: FusedSafety;
  internetSpeed: FusedMetric;
  airQuality: FusedMetric;
  costOfLiving: FusedMetric;
  events: FusedEvent;
  attraction: FusedPlace;
  communityReviews: FusedCommunity;
  safety: FusedSafety;
  emergencyContact: FusedPlace;
  visa: FusedMetric;
  simProvider: FusedPlace;
  utility: FusedPlace;
  grocery: FusedPlace;
  laundry: FusedPlace;
  gym: FusedPlace;
}
